import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getAsset, updateAsset } from '../../services/assetService';
import { getFloc } from '../../services/flocService';
import { getAssetInstallationHistory, decommissionAsset } from '../../services/assetInstallationService';
import { getAssetConditionHistory } from '../../services/conditionService';
import { uploadAssetPhoto, uploadAssetDocument, uploadAssetAudio, deleteFile } from '../../services/storageService';
import { Asset, FunctionalLocation, AssetInstallation, ConditionAssessment, AssetLifecycleStatus } from '@easydone/shared';
import { Timestamp } from 'firebase/firestore';
import { AudioRecorder } from '../../components/AudioRecorder';
import { ImageViewer } from '../../components/ImageViewer';
import { MeasurementsWidget } from '../../components/MeasurementsWidget';
import {
  MapPinIcon,
  WrenchScrewdriverIcon,
  ClockIcon,
  ArrowRightIcon,
  ChartBarIcon,
  PhotoIcon,
  DocumentIcon,
  SpeakerWaveIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

export function AssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<Asset | null>(null);
  const [currentFloc, setCurrentFloc] = useState<FunctionalLocation | null>(null);
  const [installationHistory, setInstallationHistory] = useState<AssetInstallation[]>([]);
  const [conditionHistory, setConditionHistory] = useState<ConditionAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; name: string } | null>(null);
  const [showDecommissionModal, setShowDecommissionModal] = useState(false);
  const [decommissionNotes, setDecommissionNotes] = useState('');
  const [decommissioning, setDecommissioning] = useState(false);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, [assetId, currentOrg]);

  const loadData = async () => {
    if (!assetId || !currentOrg) return;

    setLoading(true);
    setError('');

    try {
      const assetData = await getAsset(currentOrg.id, assetId);
      if (!assetData) {
        setError('Asset not found');
        return;
      }
      setAsset(assetData);

      // Load current FLOC if asset is commissioned
      if (assetData.currentFlocId) {
        const flocData = await getFloc(currentOrg.id, assetData.currentFlocId);
        setCurrentFloc(flocData);
      }

      // Load installation history
      const history = await getAssetInstallationHistory(currentOrg.id, assetId);
      setInstallationHistory(history);

      // Load condition history
      const conditions = await getAssetConditionHistory(currentOrg.id, assetId, 10);
      setConditionHistory(conditions);
    } catch (err: any) {
      console.error('Failed to load asset:', err);
      setError('Failed to load asset details');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !currentOrg || !asset || !currentUser) return;

    setUploadingPhotos(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const url = await uploadAssetPhoto(currentOrg.id, asset.id, file);
        return {
          id: `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          url,
          type: file.type,
          size: file.size,
          uploadedAt: Timestamp.fromDate(new Date()),
          uploadedBy: currentUser.uid,
        };
      });

      const newPhotos = await Promise.all(uploadPromises);
      const updatedPhotos = [...(asset.photoAttachments || []), ...newPhotos];

      await updateAsset(currentOrg.id, asset.id, { photoAttachments: updatedPhotos }, currentUser.uid);
      await loadData();
      alert(`${newPhotos.length} photo(s) uploaded successfully!`);
    } catch (error) {
      console.error('Failed to upload photos:', error);
      alert('Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !currentOrg || !asset || !currentUser) return;

    setUploadingDocuments(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const url = await uploadAssetDocument(currentOrg.id, asset.id, file);
        return {
          id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          url,
          type: file.type,
          size: file.size,
          uploadedAt: Timestamp.fromDate(new Date()),
          uploadedBy: currentUser.uid,
        };
      });

      const newDocs = await Promise.all(uploadPromises);
      const updatedDocs = [...(asset.documentAttachments || []), ...newDocs];

      await updateAsset(currentOrg.id, asset.id, { documentAttachments: updatedDocs }, currentUser.uid);
      await loadData();
      alert(`${newDocs.length} document(s) uploaded successfully!`);
    } catch (error) {
      console.error('Failed to upload documents:', error);
      alert('Failed to upload documents');
    } finally {
      setUploadingDocuments(false);
      if (documentInputRef.current) documentInputRef.current.value = '';
    }
  };

  const handleAudioSave = async (blob: Blob, duration: number, category: string) => {
    if (!currentOrg || !asset || !currentUser) return;

    try {
      const url = await uploadAssetAudio(currentOrg.id, asset.id, blob, category);
      const newRecording = {
        id: `audio-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: `Recording ${new Date().toLocaleString()}`,
        url,
        type: 'audio/webm',
        size: blob.size,
        duration,
        recordedAt: Timestamp.fromDate(new Date()),
        recordedBy: currentUser.uid,
        category: category as 'normal_operation' | 'abnormal_noise' | 'other',
      };

      const updatedAudio = [...(asset.audioRecordings || []), newRecording];
      await updateAsset(currentOrg.id, asset.id, { audioRecordings: updatedAudio }, currentUser.uid);
      await loadData();
      alert('Audio recording saved successfully!');
    } catch (error) {
      console.error('Failed to save audio:', error);
      throw error;
    }
  };

  const handleDeletePhoto = async (photoId: string, photoUrl: string) => {
    if (!currentOrg || !asset || !currentUser) return;
    if (!confirm('Are you sure you want to delete this photo?')) return;

    try {
      await deleteFile(photoUrl);
      const updatedPhotos = (asset.photoAttachments || []).filter((p) => p.id !== photoId);
      await updateAsset(currentOrg.id, asset.id, { photoAttachments: updatedPhotos }, currentUser.uid);
      await loadData();
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to delete photo');
    }
  };

  const handleDeleteDocument = async (docId: string, docUrl: string) => {
    if (!currentOrg || !asset || !currentUser) return;
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await deleteFile(docUrl);
      const updatedDocs = (asset.documentAttachments || []).filter((d) => d.id !== docId);
      await updateAsset(currentOrg.id, asset.id, { documentAttachments: updatedDocs }, currentUser.uid);
      await loadData();
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert('Failed to delete document');
    }
  };

  const handleDeleteAudio = async (audioId: string, audioUrl: string) => {
    if (!currentOrg || !asset || !currentUser) return;
    if (!confirm('Are you sure you want to delete this recording?')) return;

    try {
      await deleteFile(audioUrl);
      const updatedAudio = (asset.audioRecordings || []).filter((a) => a.id !== audioId);
      await updateAsset(currentOrg.id, asset.id, { audioRecordings: updatedAudio }, currentUser.uid);
      await loadData();
    } catch (error) {
      console.error('Failed to delete recording:', error);
      alert('Failed to delete recording');
    }
  };

  const handleDecommission = async () => {
    if (!currentOrg || !asset || !currentUser) return;

    setDecommissioning(true);
    try {
      await decommissionAsset(
        currentOrg.id,
        asset.id,
        {
          removalReason: 'decommission',
          removalNotes: decommissionNotes,
        },
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );

      setShowDecommissionModal(false);
      setDecommissionNotes('');
      await loadData();
    } catch (error) {
      console.error('Failed to decommission asset:', error);
      alert('Failed to decommission asset: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setDecommissioning(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getLifecycleColor = (status: AssetLifecycleStatus) => {
    switch (status) {
      case 'commissioned': return 'bg-green-100 text-green-800';
      case 'in_storage': return 'bg-gray-100 text-gray-800';
      case 'in_maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'decommissioned': return 'bg-red-100 text-red-800';
      case 'disposed': return 'bg-slate-100 text-slate-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'bg-green-100 text-green-800';
      case 'B': return 'bg-blue-100 text-blue-800';
      case 'C': return 'bg-yellow-100 text-yellow-800';
      case 'D': return 'bg-orange-100 text-orange-800';
      case 'F': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">{error || 'Asset not found'}</p>
          <button onClick={() => navigate('/assets')} className="btn btn-secondary mt-4">
            Back to Assets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="card mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{asset.name}</h1>
            <p className="text-gray-600 mt-1">Serial: {asset.serialNumber}</p>
            <p className="text-sm text-gray-500">Asset ID: {asset.assetId}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLifecycleColor(asset.lifecycleStatus)}`}>
            {asset.lifecycleStatus.replace('_', ' ').toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Type:</span>
            <span className="ml-2 font-medium">{asset.type}</span>
          </div>
          {asset.category && (
            <div>
              <span className="text-gray-600">Category:</span>
              <span className="ml-2 font-medium">{asset.category}</span>
            </div>
          )}
          {asset.manufacturer && (
            <div>
              <span className="text-gray-600">Manufacturer:</span>
              <span className="ml-2 font-medium">{asset.manufacturer}</span>
            </div>
          )}
          {asset.model && (
            <div>
              <span className="text-gray-600">Model:</span>
              <span className="ml-2 font-medium">{asset.model}</span>
            </div>
          )}
        </div>

        {asset.description && (
          <p className="text-gray-700 mt-4 pt-4 border-t">{asset.description}</p>
        )}
      </div>

      {/* Condition Score */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <ChartBarIcon className="w-5 h-5 mr-2" />
          Condition Assessment
        </h2>
        <div className="flex items-center space-x-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Current Score</p>
            <div className="flex items-baseline">
              <span className={`text-4xl font-bold ${
                asset.conditionScore >= 80 ? 'text-green-600' :
                asset.conditionScore >= 60 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {asset.conditionScore}
              </span>
              <span className="text-gray-400 text-lg ml-2">/ 100</span>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Grade</p>
            <span className={`inline-flex items-center px-4 py-2 rounded-lg text-2xl font-bold ${getGradeColor(asset.conditionGrade)}`}>
              {asset.conditionGrade}
            </span>
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-2">Score Range</p>
            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`absolute left-0 top-0 h-full ${
                  asset.conditionScore >= 80 ? 'bg-green-500' :
                  asset.conditionScore >= 60 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${asset.conditionScore}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Current Location */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <MapPinIcon className="w-5 h-5 mr-2" />
          Current Location
        </h2>
        {asset.lifecycleStatus === 'commissioned' && currentFloc ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{currentFloc.name}</h3>
                <p className="text-sm text-gray-600 mt-1">Code: {currentFloc.code}</p>
                <p className="text-sm text-gray-600">Type: {currentFloc.type.replace('_', ' ')}</p>
                <div className="mt-2">
                  <span className="text-sm font-medium">Criticality: </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    currentFloc.criticality === 'very_high' ? 'bg-red-100 text-red-800' :
                    currentFloc.criticality === 'high' ? 'bg-orange-100 text-orange-800' :
                    currentFloc.criticality === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    currentFloc.criticality === 'low' ? 'bg-blue-100 text-blue-800' :
                    currentFloc.criticality === 'very_low' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {currentFloc.criticality.toUpperCase()}
                  </span>
                </div>
              </div>
              <Link to={`/flocs/${currentFloc.id}`} className="btn btn-secondary text-sm">
                View FLOC
              </Link>
            </div>
          </div>
        ) : asset.lifecycleStatus === 'in_storage' ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">
              <strong>Storage Location:</strong> {asset.storageLocation || 'Not specified'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Asset is in storage and ready for commissioning
            </p>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">No current location</p>
          </div>
        )}
      </div>

      {/* Measurements */}
      <div className="mb-6">
        <MeasurementsWidget
          entityType="asset"
          entityId={asset.id}
          entityName={asset.name}
        />
      </div>

      {/* Photos */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <PhotoIcon className="w-5 h-5 mr-2" />
          Photos
        </h2>

        {/* Upload Section */}
        <div className="mb-4">
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoUpload}
            className="hidden"
          />
          <button
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhotos}
            className="btn btn-primary text-sm"
          >
            {uploadingPhotos ? 'Uploading...' : 'Upload Photos'}
          </button>
        </div>

        {/* Photos Grid */}
        {asset.photoAttachments && asset.photoAttachments.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {asset.photoAttachments.map((photo) => (
              <div key={photo.id} className="relative group">
                <div
                  onClick={() => setSelectedImage({ url: photo.url, name: photo.name })}
                  className="w-full h-32 rounded-lg border border-gray-200 cursor-pointer bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${photo.url})`,
                    imageRendering: '-webkit-optimize-contrast',
                  }}
                  role="img"
                  aria-label={photo.name}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center pointer-events-none">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(photo.id, photo.url);
                    }}
                    className="opacity-0 group-hover:opacity-100 btn bg-red-600 hover:bg-red-700 text-white text-sm pointer-events-auto"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                {photo.isNameplate && (
                  <span className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded pointer-events-none">
                    Nameplate
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No photos uploaded yet</p>
        )}
      </div>

      {/* Documents */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <DocumentIcon className="w-5 h-5 mr-2" />
          Documents
        </h2>

        {/* Upload Section */}
        <div className="mb-4">
          <input
            ref={documentInputRef}
            type="file"
            multiple
            onChange={handleDocumentUpload}
            className="hidden"
          />
          <button
            onClick={() => documentInputRef.current?.click()}
            disabled={uploadingDocuments}
            className="btn btn-primary text-sm"
          >
            {uploadingDocuments ? 'Uploading...' : 'Upload Documents'}
          </button>
        </div>

        {/* Documents List */}
        {asset.documentAttachments && asset.documentAttachments.length > 0 ? (
          <div className="space-y-2">
            {asset.documentAttachments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(doc.size)} • {typeof doc.uploadedAt === 'object' && 'toDate' in doc.uploadedAt ? (doc.uploadedAt.toDate as () => Date)().toLocaleDateString() : new Date(doc.uploadedAt as number).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary text-sm"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleDeleteDocument(doc.id, doc.url)}
                    className="btn bg-red-600 hover:bg-red-700 text-white text-sm"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No documents uploaded yet</p>
        )}
      </div>

      {/* Audio Recordings */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <SpeakerWaveIcon className="w-5 h-5 mr-2" />
          Audio Recordings
        </h2>

        {/* Recorder */}
        <div className="mb-4">
          <AudioRecorder onSave={handleAudioSave} maxDuration={120} />
        </div>

        {/* Recordings List */}
        {asset.audioRecordings && asset.audioRecordings.length > 0 && (
          <div className="mt-6 space-y-3">
            <h3 className="font-medium text-gray-900">Saved Recordings</h3>
            {asset.audioRecordings.map((recording) => (
              <div
                key={recording.id}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{recording.name}</p>
                    <p className="text-sm text-gray-500">
                      {Math.floor(recording.duration / 60)}:{(recording.duration % 60).toString().padStart(2, '0')} • {recording.category?.replace('_', ' ')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {typeof recording.recordedAt === 'object' && 'toDate' in recording.recordedAt ? (recording.recordedAt.toDate as () => Date)().toLocaleString() : new Date(recording.recordedAt as number).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteAudio(recording.id, recording.url)}
                    className="btn bg-red-600 hover:bg-red-700 text-white text-sm ml-4"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <audio controls className="w-full mt-2">
                  <source src={recording.url} type="audio/webm" />
                  Your browser does not support audio playback.
                </audio>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <WrenchScrewdriverIcon className="w-5 h-5 mr-2" />
          Actions
        </h2>
        <div className="flex flex-wrap gap-3">
          {asset.lifecycleStatus === 'in_storage' && (
            <button
              onClick={() => navigate(`/assets/${asset.id}/commission`)}
              className="btn btn-primary"
            >
              Commission to FLOC
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          )}
          {asset.lifecycleStatus === 'commissioned' && (
            <button
              onClick={() => setShowDecommissionModal(true)}
              className="btn btn-secondary"
            >
              Decommission
            </button>
          )}
          <Link
            to={`/inspections/new?assetId=${asset.id}&flocId=${asset.currentFlocId || ''}`}
            className="btn btn-secondary"
          >
            New Inspection
          </Link>
          <Link
            to={`/assets/${asset.id}/inspections`}
            className="btn btn-secondary"
          >
            View History
          </Link>
        </div>
      </div>

      {/* Installation History */}
      {installationHistory.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <ClockIcon className="w-5 h-5 mr-2" />
            Installation History
          </h2>
          <div className="space-y-3">
            {installationHistory.slice(0, 5).map((installation) => (
              <div key={installation.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      FLOC: {installation.flocId}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Installed: {typeof installation.installedAt === 'object' && 'toDate' in installation.installedAt ? (installation.installedAt.toDate as () => Date)().toLocaleDateString() : new Date(installation.installedAt as number).toLocaleDateString()}
                    </p>
                    {installation.removedAt && (
                      <p className="text-sm text-gray-600">
                        Removed: {typeof installation.removedAt === 'object' && 'toDate' in installation.removedAt ? (installation.removedAt.toDate as () => Date)().toLocaleDateString() : new Date(installation.removedAt as number).toLocaleDateString()}
                        {installation.removalReason && ` (${installation.removalReason.replace('_', ' ')})`}
                      </p>
                    )}
                    {installation.daysInstalled !== undefined && (
                      <p className="text-sm text-gray-500 mt-1">
                        Duration: {installation.daysInstalled} days
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    {installation.conditionAtInstall !== undefined && (
                      <p className="text-sm text-gray-600">
                        Install: <span className="font-medium">{installation.conditionAtInstall}</span>
                      </p>
                    )}
                    {installation.conditionAtRemoval !== undefined && (
                      <p className="text-sm text-gray-600">
                        Removal: <span className="font-medium">{installation.conditionAtRemoval}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Condition History */}
      {conditionHistory.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">Recent Condition Assessments</h2>
          <div className="space-y-2">
            {conditionHistory.map((assessment) => (
              <div key={assessment.id} className="flex justify-between items-center border-b border-gray-100 pb-2">
                <div>
                  <p className="text-sm font-medium">
                    {typeof assessment.assessedAt === 'object' && 'toDate' in assessment.assessedAt ? (assessment.assessedAt.toDate as () => Date)().toLocaleDateString() : new Date(assessment.assessedAt as number).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-gray-600">
                    {assessment.assessmentMethod.replace('_', ' ')}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`text-lg font-bold ${
                    assessment.score >= 80 ? 'text-green-600' :
                    assessment.score >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {assessment.score}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getGradeColor(assessment.grade)}`}>
                    {assessment.grade}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Back Button */}
      <div>
        <button onClick={() => navigate('/assets')} className="btn btn-secondary">
          Back to Assets
        </button>
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <ImageViewer
          imageUrl={selectedImage.url}
          imageName={selectedImage.name}
          onClose={() => setSelectedImage(null)}
        />
      )}

      {/* Decommission Modal */}
      {showDecommissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Decommission Asset</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will remove the asset from its current FLOC and mark it as decommissioned.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={decommissionNotes}
                onChange={(e) => setDecommissionNotes(e.target.value)}
                rows={3}
                className="input w-full"
                placeholder="Reason for decommissioning..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDecommission}
                disabled={decommissioning}
                className="btn btn-primary flex-1"
              >
                {decommissioning ? 'Decommissioning...' : 'Confirm Decommission'}
              </button>
              <button
                onClick={() => {
                  setShowDecommissionModal(false);
                  setDecommissionNotes('');
                }}
                disabled={decommissioning}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
